import type { ProblemDetail } from "@credit-acceptance/step6-bff-client";

const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

/**
 * Reads the HTTP status off an unknown error. Tolerant of axios-shaped
 * (`error.response.status`) and fetch-shaped (`error.status`) errors so the
 * helper works across the BFF clients we use today and whatever lands
 * tomorrow.
 * @param {unknown} error - The error to inspect.
 * @returns {number | undefined} The HTTP status, if the error carries one.
 */
function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as {
    response?: { status?: number };
    status?: number | string;
  };
  if (typeof e.response?.status === "number") return e.response.status;
  const status = Number.parseInt(String(e.status), 10);
  return Number.isNaN(status) ? undefined : status;
}

/**
 * Checks whether an unknown error came back from an HTTP layer with the
 * given status.
 * @param {unknown} error - The error to inspect.
 * @param {number} status - The HTTP status to match.
 * @returns {boolean} True when `error` carries the given status.
 */
export function isHttpError(error: unknown, status: number): boolean {
  return getHttpStatus(error) === status;
}

/**
 * Extracts the RFC 7807 `ProblemDetail` envelope from an unknown error's
 * response body, when the backend classified the failure (as step6-bff does
 * for e.g. bank-verify outcomes). Callers that want to surface a curated,
 * reason-specific message (rather than the generic fallback from
 * `getErrorMessage`) should read `reason`/`details` off this instead of
 * guessing at the raw error shape.
 * @param {unknown} error - The error to inspect.
 * @returns {ProblemDetail | undefined} The problem detail body, if present.
 */
export function getProblemDetail(error: unknown): ProblemDetail | undefined {
  if (!error || typeof error !== "object") return undefined;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== "object") return undefined;
  const problem = data as Partial<ProblemDetail>;
  return typeof problem.reason === "string" && typeof problem.title === "string"
    ? (problem as ProblemDetail)
    : undefined;
}

/**
 * Extracts a human-readable message from an unknown error. Useful when
 * surfacing `error` from a TanStack Query hook in UI without having to
 * narrow the type at every call site. HTTP errors never surface their raw
 * transport message (e.g. "Request failed with status code 503") — that's
 * an implementation detail, not something a user should see — so those
 * fall back to a generic message instead.
 * @param {unknown} error - The error to read.
 * @returns {string} A best-effort message.
 */
export function getErrorMessage(error: unknown): string {
  if (getHttpStatus(error) !== undefined) return GENERIC_ERROR_MESSAGE;
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown error";
}
